/**
 * BroadcastPopup — every logged-in staff/PM/member polls /broadcast/inbox
 * every 45s and if any unacknowledged broadcast exists, shows it as a modal.
 * Acknowledgment persists server-side so a user only sees a given message once.
 *
 * Admins can send new broadcasts via the compose button that lives in Layout.
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Megaphone } from "@phosphor-icons/react";

export default function BroadcastPopup() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const active = queue[0] || null;

  const canReceive = user && user.role !== "client";

  const poll = useCallback(async () => {
    if (!canReceive) return;
    try {
      const { data } = await http.get("/broadcast/inbox");
      setQueue(data);
    } catch (err) {
      // Silent poll error — retry next tick.
    }
  }, [canReceive]);

  useEffect(() => {
    if (!canReceive) return;
    poll();
    const t = setInterval(poll, 45_000);
    return () => clearInterval(t);
  }, [canReceive, poll]);

  const acknowledge = async () => {
    if (!active) return;
    try {
      await http.post(`/broadcast/${active.id}/ack`);
    } catch (err) {
      // If ack fails we still dismiss locally — server will retry via poll
      // the next time the user reloads.
    } finally {
      setQueue((q) => q.slice(1));
    }
  };

  if (!active) return null;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) acknowledge(); }}>
      <DialogContent
        className="rounded-none border-2 border-amber-600 shadow-brutal max-w-lg"
        data-testid="broadcast-popup"
      >
        <DialogHeader>
          <div className="label-overline text-amber-600 mb-1 flex items-center gap-1.5">
            <Megaphone size={12} weight="bold" /> Studio broadcast
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase"
                       data-testid="broadcast-subject">
            {active.subject}
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            from {active.sender_name || "Admin"}
          </div>
        </DialogHeader>

        <div className="text-sm whitespace-pre-wrap text-zinc-800 mt-4"
             data-testid="broadcast-body">
          {active.body}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-zinc-300 mt-4">
          <Button
            type="button"
            onClick={acknowledge}
            data-testid="broadcast-ack"
            className="rounded-none bg-amber-600 hover:bg-amber-700 text-white font-mono uppercase tracking-widest text-xs px-5"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
